import { useEffect, useState } from 'react';
import { studioBilling, studioBooks } from '@/lib/api';

interface BillingStatus {
  subscribed: boolean;
  status: string;
  app_count: number;
  client_count: number;
  included_credits_usd: number;
  credits_used_usd: number;
  billing_period_start?: string | null;
  billing_period_end?: string | null;
  studio_platform_fee_usd: number;
  studio_app_fee_usd: number;
  studio_client_fee_usd: number;
  books_connected?: boolean;
}

interface BooksInvoice {
  id: string;
  date: string;
  amount: number;
  status: string;
  pdf_url: string | null;
}

export function StudioBilling() {
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [invoices, setInvoices] = useState<BooksInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [s, inv] = await Promise.allSettled([
          studioBilling.status(),
          studioBooks.invoices(),
        ]);
        if (s.status === 'fulfilled') setStatus(s.value);
        if (inv.status === 'fulfilled') setInvoices((inv.value.invoices ?? []) as BooksInvoice[]);
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, []);

  async function openPortal() {
    setPortalLoading(true);
    try {
      const result = await studioBilling.portal();
      window.open(result.url, '_blank');
    } finally {
      setPortalLoading(false);
    }
  }

  if (loading) return <div className="p-8 text-muted-foreground">Loading billing…</div>;

  const monthlyEstimate = status
    ? status.studio_platform_fee_usd +
      status.app_count * status.studio_app_fee_usd +
      status.client_count * status.studio_client_fee_usd
    : 0;

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">Studio Billing</h1>
        <p className="text-muted-foreground mt-1">Subscription, usage, and invoices</p>
      </div>

      {status && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="border border-border rounded-xl p-5 bg-card">
            <p className="text-xs text-muted-foreground mb-1">Monthly Estimate</p>
            <p className="text-2xl font-bold text-foreground">${monthlyEstimate.toFixed(0)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              ${status.studio_platform_fee_usd} base + {status.app_count} apps + {status.client_count} clients
            </p>
          </div>
          <div className="border border-border rounded-xl p-5 bg-card">
            <p className="text-xs text-muted-foreground mb-1">AI Credits Used</p>
            <p className="text-2xl font-bold text-foreground">${status.credits_used_usd.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-1">of ${status.included_credits_usd} included</p>
          </div>
          <div className="border border-border rounded-xl p-5 bg-card">
            <p className="text-xs text-muted-foreground mb-1">Period Ends</p>
            <p className="text-lg font-semibold text-foreground">
              {status.billing_period_end ? new Date(status.billing_period_end).toLocaleDateString() : '—'}
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-3 mb-8">
        <button
          onClick={() => void openPortal()}
          disabled={portalLoading}
          className="bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
        >
          {portalLoading ? 'Opening…' : 'Manage Subscription'}
        </button>
      </div>

      {invoices.length > 0 && (
        <div className="border border-border rounded-xl overflow-hidden bg-card">
          <div className="px-5 py-3 border-b border-border">
            <h2 className="text-sm font-semibold text-foreground">Invoices</h2>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground">Date</th>
                <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground">Amount</th>
                <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {invoices.map((inv) => (
                <tr key={inv.id} className="hover:bg-muted/20">
                  <td className="px-4 py-3 text-muted-foreground">{new Date(inv.date).toLocaleDateString()}</td>
                  <td className="px-4 py-3 font-medium text-foreground">${(inv.amount / 100).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${inv.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {inv.pdf_url && (
                      <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                        PDF
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
